// Finance CSV export of the credit notes list: ONE ROW PER JOB (credit note position), not one
// row per credit note. Accounting / LLE (2026-10-29): a note with positions on different
// Kostenstelle / Kostenträger must be split so every amount can be assigned to its cost
// centers. The note-level columns (number, talent, agency …) repeat on every row of the note.
//
// The backend list endpoint ships `cn.items` (one per position, official cost center numbers,
// net amount, VAT rate). A note without items (none exist today) still gets one row with the
// note totals so nothing disappears from the export.

export const CREDIT_NOTE_CSV_HEADERS = [
  'Number', 'Type', 'Status', 'Job Dates', 'Created', 'Created By', 'Talent', 'Client',
  'Position', 'Agency', 'AP %', 'Quantity', 'Rate', 'Net', 'VAT', 'Gross', 'Kostenträger', 'Kostenstelle',
];

const dateDe = (d) => {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const timeDe = (d) => {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return isNaN(date.getTime()) ? '' : date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};
const money = (n) => {
  if (n == null || n === '') return '';
  const num = typeof n === 'number' ? n : parseFloat(n);
  return isNaN(num) ? String(n) : num.toFixed(2);
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const escapeCsv = (v) => {
  const s = v == null ? '' : String(v);
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
};

/** "06.10.2026" or "05.10.2026 – 09.10.2026" */
export const jobDates = (item) => {
  const start = dateDe(item.start_date);
  const end = dateDe(item.end_date);
  return start && end && start !== end ? `${start} – ${end}` : start || end;
};

/** VAT and gross of one position from its net amount and VAT rate (the note's rate as fallback). */
export const itemAmounts = (item, cn) => {
  const net = item.net_amount != null ? Number(item.net_amount) : 0;
  const rate = item.vat_rate != null ? Number(item.vat_rate) : (cn.vat_rate != null ? Number(cn.vat_rate) : 0);
  const vat = round2(net * rate / 100);
  return { net: round2(net), vat, gross: round2(net + vat) };
};

const quantityLabel = (item) => {
  if (item.quantity == null) return '';
  const q = Number(item.quantity);
  const unit = item.billing_type === 'hourly' ? 'h' : item.billing_type === 'kpi' ? 'KPI' : (q === 1 ? 'day' : 'days');
  return `${q} ${unit}`;
};

const positionLabel = (item) => {
  if (item.position) return item.position;
  if (item.function_name) return item.function_name;
  const first = String(item.description || '').split('\n')[0].trim();
  return first;
};

/**
 * Rows (arrays of cell strings) for the given credit notes, one per position.
 * `ctx` supplies the list's own display helpers so the CSV matches the screen.
 */
export const buildCreditNoteCsvRows = (notes, ctx = {}) => {
  const clientOf = ctx.getClientName || ((cn) => cn.client_name || '');
  const agencyOf = ctx.displayAgency || ((name) => (name != null && String(name).trim().length > 1 ? String(name).trim() : ''));
  const rows = [];
  (notes || []).forEach((cn) => {
    const head = [
      cn.credit_note_number || cn.id,
      cn.invoice_type || '',
      cn.status || '',
    ];
    const meta = [
      cn.created_at ? `${dateDe(cn.created_at)} ${timeDe(cn.created_at)}`.trim() : '',
      cn.created_by || '',
      cn.talent_name || '',
    ];
    const agency = [
      agencyOf(cn.agency_name),
      cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '',
    ];
    const items = Array.isArray(cn.items) ? cn.items : [];
    if (items.length === 0) {
      rows.push([
        ...head,
        cn.booking_dates ? String(cn.booking_dates).replace(/\n/g, ' | ') : dateDe(cn.invoice_date),
        ...meta,
        clientOf(cn) || '',
        '',
        ...agency,
        '', '',
        money(cn.net_amount), money(cn.vat_amount), money(cn.gross_amount),
        cn.cost_centers_2 || '', cn.cost_centers_1 || '',
      ]);
      return;
    }
    items.forEach((item) => {
      const { net, vat, gross } = itemAmounts(item, cn);
      rows.push([
        ...head,
        jobDates(item),
        ...meta,
        item.client_name || clientOf(cn) || '',
        positionLabel(item),
        ...agency,
        quantityLabel(item),
        money(item.rate),
        money(net), money(vat), money(gross),
        item.cost_center_2 || '', // Kostenträger
        item.cost_center_1 || '', // Kostenstelle
      ]);
    });
  });
  return rows;
};

/** Full CSV text (CRLF, Excel-friendly) — prefix with a BOM when writing the Blob. */
export const buildCreditNoteCsv = (notes, ctx) =>
  [CREDIT_NOTE_CSV_HEADERS.join(','), ...buildCreditNoteCsvRows(notes, ctx).map((r) => r.map(escapeCsv).join(','))].join('\r\n');
